/**
 * Dentition, FDI tooth numbering and anatomical surfaces (spec §6).
 *
 * Numbering scheme: FDI / ISO 3950 two-digit notation.
 *   Permanent: 11-18 21-28 31-38 41-48
 *   Primary:   51-55 61-65 71-75 81-85
 *
 * CLINICAL SAFETY NOTE — the single most dangerous bug available in this file
 * is swapping the patient's left and right. The chart is drawn from the
 * CLINICIAN'S point of view facing the patient, so the patient's RIGHT side
 * appears on the LEFT of the chart. `chartColumn` below is derived from the
 * quadrant, never from the UI's reading direction, so switching the interface
 * to Arabic right-to-left cannot mirror the chart.
 *
 * The surface label set is a development default. A qualified clinician must
 * confirm the labels and the per-tooth surface sets before clinical release —
 * tracked as CLIN-2 in docs/BLOCKERS.md.
 */

export type Dentition = 'permanent' | 'primary';
export type PatientSide = 'right' | 'left';
export type Arch = 'upper' | 'lower';
export type ToothType = 'incisor' | 'canine' | 'premolar' | 'molar';

export type Surface =
  | 'mesial'
  | 'distal'
  | 'buccal' // posterior facial surface
  | 'labial' // anterior facial surface
  | 'palatal' // upper lingual surface
  | 'lingual' // lower lingual surface
  | 'occlusal' // posterior biting surface
  | 'incisal'; // anterior biting edge

export interface Tooth {
  /** FDI code, e.g. "16" or "51". */
  fdi: string;
  quadrant: number;
  position: number;
  dentition: Dentition;
  arch: Arch;
  /** The PATIENT's side. Never the viewer's side. */
  patientSide: PatientSide;
  type: ToothType;
  /** Anatomically applicable surfaces for this tooth. */
  surfaces: Surface[];
  /**
   * 0-based column in a standard dental chart, left-to-right as the clinician
   * sees it. Independent of UI text direction.
   */
  chartColumn: number;
}

const PERMANENT_QUADRANTS = [1, 2, 3, 4] as const;
const PRIMARY_QUADRANTS = [5, 6, 7, 8] as const;

function archOf(q: number): Arch {
  return q === 1 || q === 2 || q === 5 || q === 6 ? 'upper' : 'lower';
}

function sideOf(q: number): PatientSide {
  // FDI quadrants 1 and 4 (and primary 5, 8) are the patient's RIGHT.
  return q === 1 || q === 4 || q === 5 || q === 8 ? 'right' : 'left';
}

function typeOf(dentition: Dentition, position: number): ToothType {
  if (dentition === 'permanent') {
    if (position <= 2) return 'incisor';
    if (position === 3) return 'canine';
    if (position <= 5) return 'premolar';
    return 'molar';
  }
  // Primary dentition has no premolars: positions 4 and 5 are molars.
  if (position <= 2) return 'incisor';
  if (position === 3) return 'canine';
  return 'molar';
}

export function surfacesFor(fdi: string): Surface[] {
  const t = toothOf(fdi);
  return t.surfaces;
}

function computeSurfaces(arch: Arch, type: ToothType): Surface[] {
  const anterior = type === 'incisor' || type === 'canine';
  return [
    'mesial',
    'distal',
    anterior ? 'labial' : 'buccal',
    arch === 'upper' ? 'palatal' : 'lingual',
    anterior ? 'incisal' : 'occlusal',
  ];
}

function build(quadrant: number, position: number, dentition: Dentition): Tooth {
  const arch = archOf(quadrant);
  const patientSide = sideOf(quadrant);
  const type = typeOf(dentition, position);
  const perArch = dentition === 'permanent' ? 8 : 5;
  // Patient's right occupies the left half of the chart; within it, the most
  // posterior tooth is outermost.
  const chartColumn =
    patientSide === 'right' ? perArch - position : perArch + (position - 1);
  return {
    fdi: `${quadrant}${position}`,
    quadrant,
    position,
    dentition,
    arch,
    patientSide,
    type,
    surfaces: computeSurfaces(arch, type),
    chartColumn,
  };
}

function buildAll(): Map<string, Tooth> {
  const m = new Map<string, Tooth>();
  for (const q of PERMANENT_QUADRANTS) {
    for (let p = 1; p <= 8; p++) {
      const t = build(q, p, 'permanent');
      m.set(t.fdi, t);
    }
  }
  for (const q of PRIMARY_QUADRANTS) {
    for (let p = 1; p <= 5; p++) {
      const t = build(q, p, 'primary');
      m.set(t.fdi, t);
    }
  }
  return m;
}

const TEETH = buildAll();

export const ALL_TEETH: readonly Tooth[] = [...TEETH.values()];

export function isValidFdi(fdi: string): boolean {
  return TEETH.has(fdi);
}

export function toothOf(fdi: string): Tooth {
  const t = TEETH.get(fdi);
  if (!t) throw new RangeError(`"${fdi}" is not a valid FDI tooth code`);
  return t;
}

export function teethFor(dentition: Dentition): Tooth[] {
  return ALL_TEETH.filter((t) => t.dentition === dentition);
}

/** Chart rows in clinician view order: upper arch then lower arch. */
export function chartRows(dentition: Dentition | 'mixed'): { upper: Tooth[]; lower: Tooth[] } {
  const pool =
    dentition === 'mixed' ? [...ALL_TEETH] : ALL_TEETH.filter((t) => t.dentition === dentition);
  const byColumn = (a: Tooth, b: Tooth) => a.chartColumn - b.chartColumn || a.fdi.localeCompare(b.fdi);
  return {
    upper: pool.filter((t) => t.arch === 'upper').sort(byColumn),
    lower: pool.filter((t) => t.arch === 'lower').sort(byColumn),
  };
}

export function assertSurfacesValid(fdi: string, surfaces: readonly Surface[]): void {
  const allowed = new Set(toothOf(fdi).surfaces);
  const bad = surfaces.filter((s) => !allowed.has(s));
  if (bad.length > 0) {
    throw new RangeError(
      `Surfaces [${bad.join(', ')}] are not anatomically applicable to tooth ${fdi} ` +
        `(allowed: ${[...allowed].join(', ')})`,
    );
  }
}

/**
 * Typical eruption windows, used ONLY to SUGGEST a starting chart. The
 * clinician must confirm what is actually erupted, retained or missing; the
 * system never infers a whole dentition from age and never rewrites an
 * existing chart when the patient has a birthday (spec §6).
 */
export function suggestDentition(ageYears: number): Dentition | 'mixed' {
  if (ageYears < 6) return 'primary';
  if (ageYears < 13) return 'mixed';
  return 'permanent';
}

/** Primary tooth that normally precedes a given permanent tooth, if any. */
export function precedingPrimary(fdi: string): string | null {
  const t = toothOf(fdi);
  if (t.dentition !== 'permanent' || t.position > 5) return null;
  const map: Record<number, number> = { 1: 5, 2: 6, 3: 7, 4: 8 };
  const primaryQuadrant = map[t.quadrant];
  return primaryQuadrant ? `${primaryQuadrant}${t.position}` : null;
}
